// This file is now used ONLY for client-side dynamic notifications (e.g. pending items based on loaded data)
// Persistent notifications (Stock Low, etc.) are fetched from the API.

import {
  stockAPI,
  ventaRemitosAPI,
  proveedoresAPI,
  systemConfigAPI,
  type NotificationConfig,
  type ProveedorRemito,
  type ProveedorPedido
} from './api';
import { isForbiddenError } from './httpErrors';
import {
  MODULE_PROVEEDORES,
  MODULE_REMITOS_VTAS,
  MODULE_STOCK,
  sessionCanAccessModule,
  type SessionModuleAccess,
} from './modulePermissions';

export interface FrontendNotification {
  id: string;
  type: 'factura_pendiente' | 'remito_pendiente' | 'remito_parcial' | 'stock_bajo' | 'stock_cero' | 'pedido_pendiente';
  title: string;
  message: string;
  severity: 'high' | 'medium' | 'low';
  timestamp: string;
  moduleRoute?: string;
  data?: any;
}

/** When API omits module_route, infer destination from notification type (same targets as dynamic alerts). */
export function fallbackModuleRouteForNotificationType(
  type: string | undefined
): string | undefined {
  switch (type) {
    case "factura_pendiente":
      return "/ventas?view=listado";
    case "remito_pendiente":
    case "remito_parcial":
      return "/remitos-vtas";
    case "stock_bajo":
      return "/stock?filter=bajo";
    case "stock_cero":
      return "/stock?filter=critico";
    case "pedido_pendiente":
      return "/proveedores";
    default:
      return undefined;
  }
}

/**
 * Default configuration if none is found in the backend
 */
const DEFAULT_CONFIG: NotificationConfig = {
  enabledTypes: {
    stockCero: true,
    stockBajo: true,
    facturaPendiente: true,
    remitoPendiente: true,
    pedidoPendiente: true,
  },
  lowStockThreshold: 5,
};

/**
 * Merge saved notification_config with defaults. Missing keys were read as `undefined` → falsy
 * and silently disabled all dynamic + stock alerts.
 * Accepts snake_case (enabled_types, stock_cero) as stored in Django JSONField.
 */
export function normalizeNotificationConfig(
  raw: NotificationConfig | Record<string, unknown> | null | undefined
): NotificationConfig {
  const d = DEFAULT_CONFIG;
  const et =
    (raw as any)?.enabledTypes ??
    (raw as any)?.enabled_types ??
    {};
  const pick = (camel: keyof NotificationConfig["enabledTypes"], snake: string): boolean => {
    if (et[camel] !== undefined && et[camel] !== null) return !!et[camel];
    if (et[snake] !== undefined && et[snake] !== null) return !!et[snake];
    return d.enabledTypes[camel];
  };
  const thr = Number(
    (raw as any)?.lowStockThreshold ?? (raw as any)?.low_stock_threshold ?? d.lowStockThreshold
  );
  return {
    enabledTypes: {
      stockCero: pick("stockCero", "stock_cero"),
      stockBajo: pick("stockBajo", "stock_bajo"),
      facturaPendiente: pick("facturaPendiente", "factura_pendiente"),
      remitoPendiente: pick("remitoPendiente", "remito_pendiente"),
      pedidoPendiente: pick("pedidoPendiente", "pedido_pendiente"),
    },
    lowStockThreshold: Number.isFinite(thr) ? thr : d.lowStockThreshold,
  };
}

function normalizeStockAlertsResponse(raw: unknown): {
  lowStock: { count: number; items: { codigo?: string; cantidad?: number; stockMinimo?: number }[] };
  zeroStock: { count: number; items: { codigo?: string; cantidad?: number }[] };
} {
  const r = raw as Record<string, any>;
  const low = r?.lowStock ?? r?.low_stock ?? { count: 0, items: [] };
  const zero = r?.zeroStock ?? r?.zero_stock ?? { count: 0, items: [] };
  return {
    lowStock: {
      count: Number(low?.count) || 0,
      items: Array.isArray(low?.items) ? low.items : [],
    },
    zeroStock: {
      count: Number(zero?.count) || 0,
      items: Array.isArray(zero?.items) ? zero.items : [],
    },
  };
}

function logUnexpectedPollError(error: unknown) {
  if (!isForbiddenError(error)) {
    console.error(error);
  }
}

export const getDynamicNotifications = async (
  facturas: any[] = [],
  access: SessionModuleAccess | null = null,
): Promise<FrontendNotification[]> => {
  const notifications: FrontendNotification[] = [];

  // Load configuration
  let config = DEFAULT_CONFIG;
  try {
    const savedConfig = await systemConfigAPI.getNotificationConfig();
    if (savedConfig) config = normalizeNotificationConfig(savedConfig as any);
  } catch (e) {
    if (!isForbiddenError(e)) {
      console.warn("Failed to load notification config, using defaults", e);
    }
  }

  const canRemitosVtas = sessionCanAccessModule(access, MODULE_REMITOS_VTAS);
  const canProveedores = sessionCanAccessModule(access, MODULE_PROVEEDORES);

  // Parallel fetch and process
  const [remitosVentaResp, remitosVentaParcialResp, remitosProvResp, pedidosProvResp] = await Promise.all([
    config.enabledTypes.remitoPendiente && canRemitosVtas
      ? ventaRemitosAPI.list({ estado: 'pendiente' }).catch((e) => { logUnexpectedPollError(e); return []; })
      : Promise.resolve([]),
    config.enabledTypes.remitoPendiente && canRemitosVtas
      ? ventaRemitosAPI.list({ estado: 'parcial' }).catch((e) => { logUnexpectedPollError(e); return []; })
      : Promise.resolve([]),
    config.enabledTypes.remitoPendiente && canProveedores
      ? proveedoresAPI.getRemitos().catch((e) => { logUnexpectedPollError(e); return [] as ProveedorRemito[]; })
      : Promise.resolve([] as ProveedorRemito[]),
    config.enabledTypes.pedidoPendiente && canProveedores
      ? proveedoresAPI.getPedidos().catch((e) => { logUnexpectedPollError(e); return [] as ProveedorPedido[]; })
      : Promise.resolve([] as ProveedorPedido[])
  ]);

  // 1. Pending Invoices (from prop) - already fast
  if (config.enabledTypes.facturaPendiente) {
    const facturasPendientes = facturas.filter(f => f.estado === 'Pendiente');
    if (facturasPendientes.length > 0) {
      const itemIds = facturasPendientes.map(f => f.id).sort().join(',');
      notifications.push({
        id: `facturas-pendientes-dynamic-${facturasPendientes.length}-${itemIds}`,
        type: 'factura_pendiente',
        title: 'Facturas Pendientes',
        message: `Hay ${facturasPendientes.length} factura${facturasPendientes.length > 1 ? 's' : ''} pendiente${facturasPendientes.length > 1 ? 's' : ''} de confirmación`,
        severity: 'high',
        timestamp: new Date().toISOString(),
        moduleRoute: '/ventas?view=listado',
        data: { count: facturasPendientes.length, items: facturasPendientes }
      });
    }
  }

  // 2. Sales Remitos
  if (remitosVentaResp.length > 0) {
    const itemIds = remitosVentaResp.map(r => r.id).sort().join(',');
    notifications.push({
      id: `remitos-venta-pendientes-dynamic-${remitosVentaResp.length}-${itemIds}`,
      type: 'remito_pendiente',
      title: 'Remitos de Venta Pendientes',
      message: `Hay ${remitosVentaResp.length} remito${remitosVentaResp.length > 1 ? 's' : ''} de venta sin entregar`,
      severity: 'medium',
      timestamp: new Date().toISOString(),
      moduleRoute: '/remitos-vtas',
      data: { count: remitosVentaResp.length, items: remitosVentaResp }
    });
  }

  if (remitosVentaParcialResp.length > 0) {
    const itemIds = remitosVentaParcialResp.map(r => r.id).sort().join(',');
    notifications.push({
      id: `remitos-venta-parciales-dynamic-${remitosVentaParcialResp.length}-${itemIds}`,
      type: 'remito_parcial',
      title: 'Entregas Parciales',
      message: `Hay ${remitosVentaParcialResp.length} remito${remitosVentaParcialResp.length > 1 ? 's' : ''} con entrega parcial`,
      severity: 'medium',
      timestamp: new Date().toISOString(),
      moduleRoute: '/remitos-vtas',
      data: { count: remitosVentaParcialResp.length, items: remitosVentaParcialResp }
    });
  }

  // 3. Supplier Remitos
  const remitosProvPendientes = remitosProvResp.filter(r => r.estado === 'pendiente' || r.estado === 'parcial');
  if (remitosProvPendientes.length > 0) {
    const itemIds = remitosProvPendientes.map(r => r.id).sort().join(',');
    notifications.push({
      id: `remitos-prov-pendientes-dynamic-${remitosProvPendientes.length}-${itemIds}`,
      type: 'remito_pendiente',
      title: 'Remitos Compra Pendientes',
      message: `Hay ${remitosProvPendientes.length} remito${remitosProvPendientes.length > 1 ? 's' : ''} de proveedor por recibir`,
      severity: 'low',
      timestamp: new Date().toISOString(),
      moduleRoute: '/proveedores',
      data: { count: remitosProvPendientes.length, items: remitosProvPendientes }
    });
  }

  // 4. Supplier Orders
  const pedidosProvPendientes = pedidosProvResp.filter(p => p.estado === 'pendiente' || (p.estado as string) === 'Pendiente');
  if (pedidosProvPendientes.length > 0) {
    const itemIds = pedidosProvPendientes.map(p => p.id).sort().join(',');
    notifications.push({
      id: `pedidos-prov-pendientes-dynamic-${pedidosProvPendientes.length}-${itemIds}`,
      type: 'pedido_pendiente',
      title: 'Pedidos Pendientes',
      message: `Hay ${pedidosProvPendientes.length} pedido${pedidosProvPendientes.length > 1 ? 's' : ''} a proveedores sin recibir`,
      severity: 'low',
      timestamp: new Date().toISOString(),
      moduleRoute: '/proveedores',
      data: { count: pedidosProvPendientes.length, items: pedidosProvPendientes }
    });
  }

  return notifications;
};

// Real-time stock alerts
export const getStockAlertNotifications = async (
  access: SessionModuleAccess | null = null,
): Promise<FrontendNotification[]> => {
  const notifications: FrontendNotification[] = [];

  if (!sessionCanAccessModule(access, MODULE_STOCK)) {
    return notifications;
  }

  let config = DEFAULT_CONFIG;
  try {
    const savedConfig = await systemConfigAPI.getNotificationConfig();
    if (savedConfig) config = normalizeNotificationConfig(savedConfig as any);
  } catch (e) {
    if (!isForbiddenError(e)) {
      console.warn("Failed to load notification config for stock alerts", e);
    }
  }

  try {
    const rawAlerts = await stockAPI.getAlerts();
    const alerts = normalizeStockAlertsResponse(rawAlerts);

    // Zero stock - high severity (id includes quantities so reset/reimport → new id)
    if (config.enabledTypes.stockCero && alerts.zeroStock.count > 0) {
      const items = alerts.zeroStock.items;
      const zeroSig =
        items.length > 0
          ? items
              .map((i) => `${String(i.codigo)}:${Number(i.cantidad)}`)
              .sort()
              .join("|")
          : `c${alerts.zeroStock.count}`;
      notifications.push({
        id: `stock-cero-dynamic-${alerts.zeroStock.count}-${zeroSig}`,
        type: "stock_cero",
        title: "Productos sin Stock",
        message: `${alerts.zeroStock.count} producto${alerts.zeroStock.count > 1 ? "s" : ""} con stock en cero`,
        severity: "high",
        timestamp: new Date().toISOString(),
        moduleRoute: "/stock?filter=critico",
        data: { count: alerts.zeroStock.count, items: items.slice(0, 5) },
      });
    }

    if (config.enabledTypes.stockBajo && alerts.lowStock.count > 0) {
      const items = alerts.lowStock.items;
      const lowSig =
        items.length > 0
          ? items
              .map((i) => `${String(i.codigo)}:${Number(i.cantidad)}`)
              .sort()
              .join("|")
          : `c${alerts.lowStock.count}`;
      notifications.push({
        id: `stock-bajo-dynamic-${alerts.lowStock.count}-${lowSig}`,
        type: "stock_bajo",
        title: "Stock Bajo",
        message: `${alerts.lowStock.count} producto${alerts.lowStock.count > 1 ? "s" : ""} con stock bajo el mínimo`,
        severity: "medium",
        timestamp: new Date().toISOString(),
        moduleRoute: "/stock?filter=bajo",
        data: { count: alerts.lowStock.count, items: items.slice(0, 5) },
      });
    }
  } catch (error) {
    if (!isForbiddenError(error)) {
      console.error("Failed to fetch stock alerts:", error);
    }
  }

  return notifications;
};

